/**
 * UISheet — generic open/close controller for the reusable .ui-sheet component.
 *
 * Markup contract (see snippets/ui-sheet.liquid):
 *   <div class="ui-sheet-overlay" data-ui-sheet-overlay="ID"></div>
 *   <div class="ui-sheet" data-ui-sheet="ID"> ... [data-ui-sheet-close] ... </div>
 *
 * Triggers anywhere on the page:  data-ui-sheet-trigger="ID"
 * Programmatic:  window.UISheet.open('ID')  /  window.UISheet.close()
 */
(function () {
  'use strict';

  var openId = null;
  var lastFocus = null;

  function sheet(id) { return document.querySelector('[data-ui-sheet="' + cssEsc(id) + '"]'); }
  function overlay(id) { return document.querySelector('[data-ui-sheet-overlay="' + cssEsc(id) + '"]'); }
  function cssEsc(s) { return String(s).replace(/["\\]/g, '\\$&'); }

  // GSAP-driven fade (matches the theme's drawer animations). Falls back to CSS.
  if (window.gsapReady && window.gsapReady.then) { window.gsapReady.then(function () {}); }
  function gsapAnim(el, ov, opening) {
    if (typeof window.gsap === 'undefined') {
      if (window.gsapReady && window.gsapReady.then) window.gsapReady.then(function () {});
      return; // CSS handles this transition
    }
    // Animate ONLY the overlay opacity via GSAP. The panel's show/hide
    // (slide + visibility) is entirely CSS-driven via [aria-hidden] so GSAP
    // never leaves inline visibility/opacity residue on the sheet that would
    // keep a "closed" sheet visible.
    if (opening) {
      if (ov) gsap.fromTo(ov, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.25, ease: 'power2.out' });
      // Subtle staggered reveal of the sheet's content rows. Skip the quick-view
      // sheet — it manages its own media/content entrance after fetch.
      if (el && !el.querySelector('[data-qv-root]') && !el.hasAttribute('data-no-stagger')) {
        var host = el.querySelector('.ui-sheet__body') || el;
        var rows = host.children;
        if (rows && rows.length) {
          gsap.fromTo(rows, { autoAlpha: 0, y: 12 },
            { autoAlpha: 1, y: 0, duration: 0.4, ease: 'power2.out', stagger: 0.04, delay: 0.05, overwrite: true, clearProps: 'transform' });
        }
      }
    } else if (ov) {
      gsap.to(ov, { autoAlpha: 0, duration: 0.2, ease: 'power2.in' });
    }
  }

  function open(id, opts) {
    opts = opts || {};
    var el = sheet(id);
    if (!el) return;
    if (openId && openId !== id) close();
    var ov = overlay(id);
    lastFocus = document.activeElement;
    if (opts.title != null) {
      var t = el.querySelector('[data-ui-sheet-title]');
      if (t) t.textContent = opts.title;
    }
    el.setAttribute('aria-hidden', 'false');
    if (ov) ov.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('ui-sheet-open');
    openId = id;
    gsapAnim(el, ov, true);
    // focus first focusable
    requestAnimationFrame(function () {
      var f = el.querySelector('[data-ui-sheet-autofocus]') ||
        el.querySelector('input, textarea, select, button:not([data-ui-sheet-close])');
      if (f) try { f.focus(); } catch (e) {}
    });
    document.dispatchEvent(new CustomEvent('ui-sheet:open', { detail: { id: id } }));
  }

  function close() {
    if (!openId) return;
    var id = openId;
    var el = sheet(id);
    var ov = overlay(id);
    if (el) el.setAttribute('aria-hidden', 'true');
    if (ov) ov.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('ui-sheet-open');
    openId = null;
    if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (e) {} }
    lastFocus = null;
    document.dispatchEvent(new CustomEvent('ui-sheet:close', { detail: { id: id } }));
  }

  // Delegated events
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-ui-sheet-trigger]');
    if (trigger) {
      e.preventDefault();
      open(trigger.getAttribute('data-ui-sheet-trigger'));
      return;
    }
    if (e.target.closest('[data-ui-sheet-close]')) { e.preventDefault(); close(); return; }
    var ov = e.target.closest('[data-ui-sheet-overlay]');
    if (ov && ov.getAttribute('aria-hidden') === 'false') { close(); }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openId) { close(); }
  });

  window.UISheet = { open: open, close: close, current: function () { return openId; } };
})();
